import { useAction, useAtom } from '@reatom/npm-react'
import { memo } from 'react'
import styled from 'styled-components'

import {
  boardDisplayAtom,
  resetBoardDisplayAction,
  setBoardDensityAction,
  toggleBoardColumnAction,
  toggleBoardFieldAction,
} from '../../model/board-display-atom'

import type { ColumnKey, FieldKey } from '../../model/board-display'

const COLUMNS: ReadonlyArray<{ key: ColumnKey; label: string }> = [
  { key: 'new', label: 'New pairs' },
  { key: 'finalStretch', label: 'Final stretch' },
  { key: 'migrated', label: 'Migrated' },
]

const FIELDS: ReadonlyArray<{ key: FieldKey; label: string }> = [
  { key: 'socials', label: 'Socials' },
  { key: 'risk', label: 'Risk chips' },
  { key: 'netBought', label: 'Net bought' },
  { key: 'address', label: 'Contract address' },
  { key: 'bonding', label: 'Bonding ring' },
  { key: 'creator', label: "Creator's record" },
]

/**
 * What the board shows, chosen by whoever is reading it.
 *
 * Everything here is presentation, and every switch has something behind it —
 * there is no ranking section, because the three feeds on this board each
 * arrive in an order of their own and none of them takes a strategy.
 *
 * Design: https://claude.ai/artifact/5DHS184wUvyGV3PGQTbvLS
 */
export const PulseDisplay: React.FC<{ onClose: () => void }> = memo(
  ({ onClose }) => {
    const [display] = useAtom(boardDisplayAtom)
    const setDensity = useAction(setBoardDensityAction)
    const toggleColumn = useAction(toggleBoardColumnAction)
    const toggleField = useAction(toggleBoardFieldAction)
    const reset = useAction(resetBoardDisplayAction)

    const onlyOneLeft =
      COLUMNS.filter(({ key }) => display.columns[key]).length === 1

    return (
      <>
        <Backdrop onClick={onClose} />
        <Menu aria-label="Display settings">
          <div className="dsec">
            <span className="lb">Density</span>
            <div className="dseg">
              <button
                className={display.density === 'compact' ? 'on' : undefined}
                type="button"
                onClick={() => setDensity('compact')}
              >
                Compact
              </button>
              <button
                className={display.density === 'regular' ? 'on' : undefined}
                type="button"
                onClick={() => setDensity('regular')}
              >
                Regular
              </button>
            </div>
          </div>

          <div className="dsec">
            <span className="lb">Columns</span>
            {COLUMNS.map(({ key, label }) => {
              const on = display.columns[key]
              /* The menu lives on the board, so the last column cannot go. */
              const locked = on && onlyOneLeft

              return (
                <label className="drow" key={key} data-locked={locked}>
                  <span
                    className="dbox"
                    style={{
                      background: on ? 'var(--t1)' : 'transparent',
                      borderColor: on ? 'var(--t1)' : 'var(--t5)',
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={on ? 'var(--bg)' : 'transparent'}
                      strokeWidth="3.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 13l5 5L20 6" />
                    </svg>
                  </span>
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={locked}
                    onChange={() => toggleColumn(key)}
                  />
                  <span style={{ color: on ? 'var(--t1)' : 'var(--t3)' }}>
                    {label}
                  </span>
                  {locked ? <span className="dnote">last one</span> : undefined}
                </label>
              )
            })}
          </div>

          <div className="dsec">
            <span className="lb">On each row</span>
            {FIELDS.map(({ key, label }) => {
              const on = display.fields[key]

              return (
                <label className="drow" key={key}>
                  <span
                    className="dtr"
                    style={{
                      background: on
                        ? 'rgba(245,245,245,.9)'
                        : 'var(--chip-hi)',
                      justifyContent: on ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <span
                      className="dkn"
                      style={{ background: on ? 'var(--bg)' : 'var(--t4)' }}
                    />
                  </span>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleField(key)}
                  />
                  <span style={{ color: on ? 'var(--t1)' : 'var(--t3)' }}>
                    {label}
                  </span>
                </label>
              )
            })}
          </div>

          <div className="dfoot">
            <span>Saved to this browser</span>
            <button type="button" onClick={() => reset()}>
              Reset
            </button>
          </div>
        </Menu>
      </>
    )
  },
)

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 5;
`

const Menu = styled.div`
  position: absolute;
  z-index: 6;
  top: calc(100% + 6px);
  right: 0;
  width: 264px;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 6px;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-align: left;

  .dsec {
    padding: 9px 8px 10px;
    border-bottom: 1px solid var(--line-soft);
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .dsec:last-of-type {
    border-bottom: 0;
  }

  .dsec > .lb {
    padding: 0 4px;
    height: 13px;
  }

  .dseg {
    display: flex;
    align-items: center;
    gap: 1px;
    padding: 2px;
    border-radius: 5px;
    background: var(--head);
  }

  .dseg button {
    flex-grow: 1;
    height: 22px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
    color: var(--t4);
  }

  .dseg button.on {
    background: var(--chip-hi);
    color: var(--t1);
  }

  .drow {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 24px;
    padding: 0 6px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 11px;
    font-weight: 500;
  }

  .drow:hover {
    background: var(--chip);
  }

  .drow[data-locked='true'] {
    cursor: default;
  }

  .drow input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
  }

  .dnote {
    margin-left: auto;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--t5);
  }

  .dbox {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    border-radius: 3px;
    border: 1px solid;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dbox svg {
    width: 9px;
    height: 9px;
  }

  .dtr {
    flex-shrink: 0;
    width: 24px;
    height: 14px;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    padding: 2px;
  }

  .dkn {
    width: 10px;
    height: 10px;
    border-radius: 9999px;
  }

  .dfoot {
    padding: 8px;
    border-top: 1px solid var(--line-soft);
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    font-weight: 400;
    color: var(--t4);
  }

  .dfoot button {
    margin-left: auto;
    height: 22px;
    padding: 0 8px;
    border-radius: 4px;
    background: var(--chip);
    color: var(--t3);
    font-size: 10px;
    font-weight: 500;
  }

  .dfoot button:hover {
    color: var(--t1);
  }
`
